import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Table,
    UncontrolledDropdown
} from "reactstrap";
import Swal from "sweetalert2";
import UserStatusBadge from "../../Components/Badges/UserStatusBadge";
import ButtonBack from "../../Components/ButtonBack";
import { useReferralLinkBuilder } from "../../hooks/useReferralLinkBuilder";
import { adminChangeUserPassword, adminLoginAsUser, getAppUserById, getUserBillingHistory, grantFreePass, cancelImmediatelySubscription, reactivateSubscription, getPaymentMethod, getDexSchedule } from "../../slices/admin/thunk";
import BaxRxPlusSummaryCard from "./BaxRxPlus";
import BaxRxPlusManager from "./BaxRxPlusManager";
import MemberCards from "./MemberCards";
import moment from "moment";
import SavedDrugsList from "./SavedDrugsList";

const AppUserInfoPage = () => {
    const { userId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // State to manage user data and loading/error states

    const { getMemberPortalBaseUrl } = useReferralLinkBuilder();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [managerOpen, setManagerOpen] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [dexSchedule, setDexSchedule] = useState(null);
    const [dexScheduleLoading, setDexScheduleLoading] = useState(false);
    const [changing, setChanging] = useState(false);


    // Fetch user data on component mount
    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) {
                setError("User ID is required");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await dispatch(getAppUserById(userId));

                if (response.payload) {
                    setUser(response.payload);
                } else {
                    setError("User not found");
                }
            } catch (err) {
                console.error("Error fetching user:", err);
                setError("Failed to load user data");
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [dispatch, userId]);

    useEffect(() => {
        if (userId) {
            fetchBilling();
            fetchPaymentMethod();
            fetchDexSchedule();
        }
    }, [userId]);

    useEffect(() => {
        if (managerOpen && userId) {
            fetchBilling();
            fetchPaymentMethod();
            fetchDexSchedule();
        }
    }, [managerOpen]);

    const refreshUserData = async () => {
        try {
            const response = await dispatch(getAppUserById(userId));
            if (response.payload) {
                setUser(response.payload);
            }
        } catch (err) {
            console.error("Error refreshing user data:", err);
        }
    };

    const fetchBilling = async () => {
        if (!userId) return;
        try {
            setBillingLoading(true);
            setBillingError(null);

            // Llamada al thunk
            const res = await dispatch(getUserBillingHistory({ userId }));


            const payload = res?.payload ?? {};
            setInvoices(payload);
        } catch (e) {
            console.error("Error fetching billing history:", e);
            setBillingError(e?.message || "Failed to load billing history");
        } finally {
            setBillingLoading(false);
        }
    };

    const fetchPaymentMethod = async () => {
        if (!userId) return;
        try {
            const res = await dispatch(getPaymentMethod({ userId }));
            const payload = res?.payload ?? null;
            setPaymentMethod(payload);
        } catch (e) {
            console.error("Error fetching payment method:", e);
            setPaymentMethod(null);
        }
    };

    const fetchDexSchedule = async () => {
        if (!userId) return;
        try {
            setDexScheduleLoading(true);
            const res = await dispatch(getDexSchedule({ userId }));
            const payload = res?.payload ?? null;
            setDexSchedule(payload);
        } catch (e) {
            console.error("Error fetching DEX schedule:", e);
            setDexSchedule(null);
        } finally {
            setDexScheduleLoading(false);
        }
    };

    const handleEditClick = () => {
        setOpenEditModal(true);
    };

    async function handleSendPasswordReset() {
        const email = user?.email;
        const auth = getAuth(); // your initialized app
        try {

            await sendPasswordResetEmail(auth, email);
            // Note: resolves even if email doesn't exist (prevents enumeration)

            await Swal.fire({
                title: 'Password Reset Email Sent',
                text: `If ${email} is registered, a reset link has been sent.`,
                icon: 'success',
                confirmButtonText: 'OK'
            });

            return { ok: true };
        } catch (e) {
            // Common gotchas: invalid-email, missing API key/project config, etc.
            console.error(e);
            await Swal.fire({
                title: 'Error Sending Reset Email',
                text: e.message || "Please try again later.",
                icon: 'error',
                confirmButtonText: 'OK'
            });

            return { ok: false, error: e?.message || "Failed to send reset email" };
        }
    }

    const handleChangePassword = async () => {
        try {
            if (!user?.id) {
                Swal.fire("Error", "Missing user UID", "error");
                return;
            }

            const { value: newPassword } = await Swal.fire({
                title: "Set New Password",
                input: "password",
                inputLabel: "New password",
                inputAttributes: { autocapitalize: "off", autocorrect: "off" },
                inputValidator: (v) => (!v || v.length < 4 ? "Min 4 characters" : undefined),
                showCancelButton: true,
                confirmButtonText: "Update",
            });
            if (!newPassword) return;

            setChanging(true);
            await adminChangeUserPassword({ userId: user.id, newPassword });

            Swal.fire("Updated", "Password changed and sessions revoked", "success");
            // Optionally:
            // await refreshUserData();
        } catch (e) {
            Swal.fire("Error", e.message || "Could not change password", "error");
        } finally {
            setChanging(false);
        }
    };



    const handleLoginAsUser = async () => {
        // Show loading dialog
        Swal.fire({
            title: 'Logging in as user...',
            text: 'Please wait while we authenticate you as this user.',
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {

            console.log("Logging in as user:", userId);

            const response = await dispatch(adminLoginAsUser({ userId }));

            if (response.payload && response.payload.userAccessToken) {
                // Successfully got the token
                const token = response.payload.userAccessToken;

                // Store the token (adjust based on your auth system)
                // localStorage.setItem('authToken', token);
                // or however you handle token storage in your app

                // Open link in a new tab
                const baseUrl = getMemberPortalBaseUrl();

                const finalUrl = `${baseUrl}/login?token=${token}`;

                // Open in new tab
                window.open(finalUrl, '_blank');

                // Update user state
                // Success message
                await Swal.fire({
                    title: 'Success!',
                    text: `You are now logged in as ${user.email || 'the user'}`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                // No token received
                throw new Error(response.payload?.message || "User not found or login failed");
            }
        } catch (err) {
            console.error("Error logging in as user:", err);

            // Show error message
            await Swal.fire({
                title: 'Error',
                text: err.message || "Failed to login as user. Please try again.",
                icon: 'error',
                confirmButtonText: 'OK'
            });

        } finally {
        }
    };

    const renderUserType = (type) => {
        const color = type === "employee" ? "info" : "secondary";
        return (
            <Badge color={color} pill className="text-capitalize">
                {type}
            </Badge>
        );
    };

    const renderSubscriptionStatus = (status) => {
        const isActive = status === "active";
        const isCancelling = user?.subscriptionCancelAtPeriodEnd === true;
        const expirationDate = user?.subscriptionExpirationDate;

        return (
            <div className="d-flex flex-column">
                <div className="mb-1">
                    <UserStatusBadge
                        status={isActive ? "Active" : "Inactive"}
                        color={isActive ? "success" : "danger"}
                    />
                </div>
                {isCancelling && isActive && (
                    <div className="d-flex flex-column">
                        <Badge color="warning" pill className="mb-1">
                            <i className="ri-alarm-warning-line me-1"></i>
                            Cancelling
                        </Badge>
                        {expirationDate && (
                            <small className="text-muted">
                                Expires: {new Date(expirationDate).toLocaleDateString()}
                            </small>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderUplineMetadata = (uplineMetadata) => {
        if (!uplineMetadata) return null;

        const uplineItems = [];

        if (uplineMetadata.nationalId) {
            uplineItems.push({
                id: uplineMetadata.nationalId,
                role: 'National',
                path: `/national/${uplineMetadata.nationalId}`,
                color: 'primary',
                icon: 'ri-shield-star-line'
            });
        }

        if (uplineMetadata.gaId) {
            uplineItems.push({
                id: uplineMetadata.gaId,
                role: 'GA',
                path: `/ga/${uplineMetadata.gaId}`,
                color: 'info',
                icon: 'ri-user-star-line'
            });
        }

        if (uplineMetadata.brokerId) {
            uplineItems.push({
                id: uplineMetadata.brokerId,
                role: 'Broker',
                path: `/brokers/${uplineMetadata.brokerId}`,
                color: 'success',
                icon: 'ri-briefcase-line'
            });
        }

        if (uplineItems.length === 0) return null;

        return (
            <div className="mb-3">
                <span className="fw-medium d-block mb-2">Upline Hierarchy:</span>
                <div className="d-flex flex-wrap gap-2">
                    {uplineItems.map((item, index) => (
                        <Badge
                            key={item.id}
                            color={item.color}
                            pill
                            className="cursor-pointer d-flex align-items-center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(item.path)}
                        >
                            <i className={`${item.icon} me-1`}></i>
                            {item.role}
                        </Badge>
                    ))}
                </div>
            </div>
        );
    };


    const renderDropdownMenu = () => {
        return (
            <DropdownMenu>
                <DropdownItem onClick={handleLoginAsUser} className="edit-item-btn">
                    <i className="ri-login-box-fill align-bottom me-2 text-muted"></i>
                    Login as User
                </DropdownItem>
                {/* <DropdownItem onClick={handleEditClick} className="edit-item-btn">
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                    Edit Information
                </DropdownItem> */}
                <DropdownItem onClick={handleChangePassword}>
                    <i className="ri-lock-fill align-bottom me-2 text-muted"></i>
                    Change Password
                </DropdownItem>
                <DropdownItem onClick={handleSendPasswordReset}>
                    <i className="ri-mail-send-fill align-bottom me-2 text-muted"></i>
                    Send Password Reset
                </DropdownItem>
            </DropdownMenu>
        );
    };

    if (loading) {
        return (
            <div className="page-content">
                <div
                    className="text-center d-flex justify-content-center align-items-center"
                    style={{ minHeight: "150px", height: "150px" }}
                >
                    <div className="spinner-border spinner-border-lg mr-3"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <ButtonBack />
                        </div>
                        <div className="text-center py-5">
                            <i className="ri-error-warning-line text-danger mb-3" style={{ fontSize: "3rem" }}></i>
                            <h5 className="text-danger mb-2">Error Loading User</h5>
                            <p className="text-muted mb-3">{error}</p>
                            <Button color="primary" onClick={() => window.location.reload()}>
                                Try Again
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <ButtonBack />
                        </div>
                        <div className="text-center py-5">
                            <i className="ri-user-line text-muted mb-3" style={{ fontSize: "3rem" }}></i>
                            <h5 className="text-muted mb-2">User Not Found</h5>
                            <p className="text-muted">The requested user could not be found.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="page-content">
                <div className="card">
                    <div className="card-body pb-5">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <ButtonBack />
                            <UncontrolledDropdown className="dropdown d-inline-block">
                                <DropdownToggle
                                    tag="button"
                                    className="btn btn-soft-primary fw-normal border border-primary btn-xl"
                                >
                                    Manage User
                                </DropdownToggle>
                                {renderDropdownMenu()}
                            </UncontrolledDropdown>
                        </div>

                        <Row>
                            {/* User Basic Information */}
                            <Col xl={4}>
                                <Card className="border">
                                    <CardHeader style={{ background: "var(--thead-bg)" }}>
                                        <h5 className="mb-0">User Information</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <div className="mb-3">
                                            <h4 className="mb-2">{user.name}</h4>
                                            <h6 className="mb-3 fw-normal text-muted">{user.email}</h6>

                                            <div className="mb-2">
                                                <span className="fw-medium">Member ID: </span>
                                                <span className="text-muted">#{user.memberId}</span>
                                            </div>

                                            <div className="mb-2 d-flex align-items-center">
                                                <span className="fw-medium me-2">Type:</span>
                                                {renderUserType(user.type)}
                                            </div>

                                            <div className="mb-2 d-flex align-items-center">
                                                <span className="fw-medium me-2">Subscription:</span>
                                                {renderSubscriptionStatus(user.baxrxPlus)}
                                            </div>

                                            {user.upline && (
                                                <div className="mb-2">
                                                    <Badge color="warning" pill>
                                                        <i className="ri-star-fill me-1"></i>
                                                        Upline
                                                    </Badge>
                                                </div>
                                            )}

                                            {user.dependentContext?.isDependent && (
                                                <div className="mb-3">
                                                    <Badge color="info" pill className="mb-2">
                                                        <i className="ri-user-shared-line me-1"></i>
                                                        Dependent Account
                                                    </Badge>
                                                    {user.dependentContext.primaryMember && (
                                                        <div className="mt-2 p-2 bg-light rounded">
                                                            <small className="fw-medium d-block mb-1">Primary Account Holder:</small>
                                                            <small className="text-muted d-block">{user.dependentContext.primaryMember.name}</small>
                                                            <small className="text-muted d-block">{user.dependentContext.primaryMember.email}</small>
                                                            <small className="text-muted">Member ID: #{user.dependentContext.primaryMember.memberId}</small>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {renderUplineMetadata(user.uplineMetadata)}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>

                            {/* Cards Section */}
                            <MemberCards user={user} />

                            <Col xl={4}>
                                <BaxRxPlusSummaryCard
                                    status={user.baxrxPlus || "none"}      // "active" | "canceled" | "past_due" | "incomplete" | "none"
                                    eligibilityNote={user.eligibilityNote} // opcional
                                    onManage={() => setManagerOpen(true)}
                                    user={user}
                                />

                                <BaxRxPlusManager
                                    isOpen={managerOpen}
                                    onClose={() => setManagerOpen(false)}
                                    status={user.baxrxPlus || "none"}
                                    eligibilityNote={user.eligibilityNote}
                                    billingHistory={invoices || []}
                                    paymentMethod={paymentMethod}
                                    subscriptionCancelAtPeriodEnd={user.subscriptionCancelAtPeriodEnd}
                                    subscriptionExpirationDate={user.subscriptionExpirationDate}
                                    dexSchedule={dexSchedule}
                                    dexScheduleLoading={dexScheduleLoading}
                                    user={user}

                                    onUpdateEligibility={(note) => api.updateEligibility(user.id, note)}
                                    onUpdatePaymentMethod={(pmToken) => api.updatePayment(user.id, pmToken)}
                                    onCancelSubscription={async () => {
                                        const result = await Swal.fire({
                                            title: "Cancel at Period End?",
                                            text: "The subscription will remain active until the end of the current billing period.",
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonColor: "#d33",
                                            cancelButtonColor: "#6c757d",
                                            confirmButtonText: "Yes, cancel at period end",
                                            cancelButtonText: "No, keep it"
                                        });

                                        if (result.isConfirmed) {
                                            try {
                                                await dispatch(cancelImmediatelySubscription({
                                                    userId: user.id,
                                                    immediate: false
                                                })).unwrap();
                                                await refreshUserData();
                                                Swal.fire({
                                                    title: "Scheduled for Cancellation!",
                                                    text: "Subscription will be cancelled at the end of the billing period",
                                                    icon: "success",
                                                    timer: 2000,
                                                    showConfirmButton: false
                                                });
                                            } catch (error) {
                                                Swal.fire({
                                                    title: "Error!",
                                                    text: error.message || "Failed to cancel subscription",
                                                    icon: "error",
                                                    confirmButtonText: "Ok"
                                                });
                                            }
                                        }
                                    }}
                                    onCancelImmediately={async () => {
                                        const result = await Swal.fire({
                                            title: "Cancel Immediately?",
                                            text: "This will terminate the subscription and access right away. The user will lose access to BaxRx+ immediately.",
                                            icon: "error",
                                            showCancelButton: true,
                                            confirmButtonColor: "#dc3545",
                                            cancelButtonColor: "#6c757d",
                                            confirmButtonText: "Yes, cancel immediately",
                                            cancelButtonText: "No, go back"
                                        });

                                        if (result.isConfirmed) {
                                            try {
                                                await dispatch(cancelImmediatelySubscription({
                                                    userId: user.id,
                                                    immediate: true
                                                })).unwrap();
                                                await refreshUserData();
                                                Swal.fire({
                                                    title: "Cancelled!",
                                                    text: "Subscription has been cancelled immediately",
                                                    icon: "success",
                                                    timer: 2000,
                                                    showConfirmButton: false
                                                });
                                            } catch (error) {
                                                Swal.fire({
                                                    title: "Error!",
                                                    text: error.message || "Failed to cancel subscription immediately",
                                                    icon: "error",
                                                    confirmButtonText: "Ok"
                                                });
                                            }
                                        }
                                    }}
                                    onReactivateSubscription={async () => {
                                        const result = await Swal.fire({
                                            title: "Reactivate Subscription?",
                                            text: "This will reactivate the user's BaxRx+ subscription.",
                                            icon: "question",
                                            showCancelButton: true,
                                            confirmButtonColor: "#28a745",
                                            cancelButtonColor: "#6c757d",
                                            confirmButtonText: "Yes, reactivate",
                                            cancelButtonText: "Cancel"
                                        });

                                        if (result.isConfirmed) {
                                            try {
                                                await dispatch(reactivateSubscription({
                                                    userId: user.id
                                                })).unwrap();
                                                await refreshUserData();
                                                Swal.fire({
                                                    title: "Reactivated!",
                                                    text: "Subscription has been reactivated successfully",
                                                    icon: "success",
                                                    timer: 2000,
                                                    showConfirmButton: false
                                                });
                                            } catch (error) {
                                                Swal.fire({
                                                    title: "Error!",
                                                    text: error.message || "Failed to reactivate subscription",
                                                    icon: "error",
                                                    confirmButtonText: "Ok"
                                                });
                                            }
                                        }
                                    }}
                                    onGrantFreePass={async () => {
                                        try {
                                            await dispatch(grantFreePass({
                                                userId: user.id,
                                                reason: 'Admin granted free access via dashboard'
                                            })).unwrap();
                                            await refreshUserData();
                                            Swal.fire({
                                                title: "Success!",
                                                text: "Free pass granted successfully",
                                                icon: "success",
                                                timer: 2000,
                                                showConfirmButton: false
                                            });
                                        } catch (error) {
                                            Swal.fire({
                                                title: "Error!",
                                                text: error.message || "Failed to grant free pass",
                                                icon: "error",
                                                confirmButtonText: "Ok"
                                            });
                                        }
                                    }}

                                    onRefreshDexSchedule={fetchDexSchedule}

                                    billingLoading={billingLoading}          // <- opcional
                                    billingError={billingError}              // <- opcional

                                    onRequestBillingPage={async (nextPage) => {
                                        // tu thunk debería aceptar { userId, page, pageSize }
                                        await dispatch(getUserBillingHistory({ userId: user.id, page: nextPage, pageSize: billingState.pageSize }));
                                    }}


                                />
                            </Col>
                            {/* BaxRx+ Section */}

                        </Row>

                        <Row className="mt-4">
                            {/* Analytics Section */}
                            <Col xl={6}>
                                <Card className="border">
                                    <CardHeader style={{ background: "var(--thead-bg)" }}>
                                        <h5 className="mb-0">User Analytics</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Table responsive className="table-borderless mb-0">
                                            <tbody>
                                                {user.lastLogin && (
                                                    <tr>
                                                        <td className="fw-medium">Last Login:</td>
                                                        <td className="text-muted">{moment.unix(user.lastLogin).format("MMM D, YYYY h:mm A")}</td>
                                                    </tr>
                                                )}

                                                <tr>
                                                    <td className="fw-medium">App Downloaded:</td>
                                                    <td className="text-muted">Coming soon</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-medium">App Type:</td>
                                                    <td className="text-muted">Coming soon</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-medium">Total Logins:</td>
                                                    <td className="text-muted">Coming soon</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>

                            {/* Claims Section */}
                            <Col xl={6}>
                                <Card className="border">
                                    <CardHeader style={{ background: "var(--thead-bg)" }}>
                                        <h5 className="mb-0">Claims History</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <div className="text-center py-4">
                                            <i className="ri-file-list-3-line text-muted mb-3" style={{ fontSize: "2rem" }}></i>
                                            <p className="text-muted mb-0">No claims data available yet</p>
                                            <small className="text-muted">Claims history will appear here once available</small>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            {/* Saved Drugs Section */}
                            <Col xl={12}>
                                <SavedDrugsList
                                    homeRetailDrug={user.homeRetailDrug}
                                    savedMeds={user.savedMeds}
                                />
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppUserInfoPage;